import { Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { COLORS } from '../IndividualPlan/tokens';
import { columnSignature, readSisColumns, saveSisMapping, type SisMapping } from '../Upload/sisFieldMapping';
import { hydrateSisMappings, sisMappingSaveSettled } from './sisMappingApi';
import { CancelMatchDialog } from './CancelMatchDialog';
import { MatchRosterFields, seedMapping } from './MatchRosterFields';
import { invalidateSisRoster, type UploadKind } from './uploadedFiles';
import { fetchUploadCsv } from './uploadsApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const SUBMIT_DISABLED_BG = '#C1C1CD';
const SUBMIT_DISABLED_FG = '#7F7F99';

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; name: string; kind?: UploadKind; csv: string };

export function MatchRosterPage() {
  const navigate = useNavigate();
  const { uploadId } = useParams<{ uploadId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [mapping, setMapping] = useState<SisMapping>({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const leftRef = useRef(false);

  useEffect(() => {
    leftRef.current = false;
    return () => {
      leftRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!uploadId) return;
    let live = true;
    Promise.all([fetchUploadCsv(uploadId), hydrateSisMappings()])
      .then(([content]) => {
        if (!live) return;
        setState({ status: 'ready', name: content.name, kind: content.kind, csv: content.csv });
        setMapping(seedMapping(readSisColumns(content.csv)));
      })
      .catch(() => {
        if (live) setState({ status: 'error' });
      });
    return () => {
      live = false;
    };
  }, [uploadId]);

  const csv = state.status === 'ready' ? state.csv : '';
  const columns = useMemo(() => (csv ? readSisColumns(csv) : []), [csv]);

  const rosterKind =
    state.status === 'ready' && (state.kind === undefined || state.kind === 'student_roster');
  const canSubmit =
    state.status === 'ready' && rosterKind && mapping.student_id !== undefined && !submitting;

  const onSubmit = async () => {
    if (state.status !== 'ready' || !uploadId) return;
    setSubmitting(true);
    setSaveFailed(false);
    const headers = columns.map((c) => c.header);
    saveSisMapping(headers, mapping);
    invalidateSisRoster();
    const saved = await sisMappingSaveSettled(columnSignature(headers));
    if (leftRef.current) return;
    if (!saved) {
      setSubmitting(false);
      setSaveFailed(true);
      return;
    }
    navigate('/data', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <title>Match your roster columns</title>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="data" />
      <Box component="main" sx={{ flex: 1, padding: '40px 96px 80px' }}>
        <Box
          sx={{
            maxWidth: 720,
            mx: 'auto',
            padding: '24px',
            bgcolor: '#FFFFFF',
            border: `1px solid ${COLORS.divider}`,
            borderRadius: '16px',
          }}
        >
          <Typography sx={{ fontSize: 28, fontWeight: 600, color: COLORS.ink }}>
            Match your roster columns
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 16, color: COLORS.muted }}>
            Tell us which of your columns hold each field we need. We&rsquo;ll remember these
            for next time you upload the same export and it will map itself.
          </Typography>

          {state.status === 'loading' && (
            <Typography role="status" aria-busy sx={{ mt: 2, fontSize: 16, color: COLORS.muted }}>
              Loading your file&hellip;
            </Typography>
          )}
          {state.status === 'error' && (
            <Typography role="alert" sx={{ mt: 2, fontSize: 16, color: COLORS.ink }}>
              We couldn&rsquo;t load this file.
            </Typography>
          )}
          {state.status === 'ready' && (
            <MatchRosterFields
              name={state.name}
              kind={state.kind}
              csv={state.csv}
              mapping={mapping}
              onMappingChange={setMapping}
              disabled={submitting}
            />
          )}

          {saveFailed && (
            <Typography role="alert" sx={{ mt: 2, fontSize: 16, color: COLORS.ink }}>
              Your column matching couldn’t be saved. Try again.
            </Typography>
          )}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => setCancelOpen(true)}
              data-upload-id={uploadId}
              sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.navy }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={onSubmit}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderRadius: '8px',
                bgcolor: COLORS.navy,
                '&:hover': { bgcolor: '#222270' },
                '&.Mui-disabled': { bgcolor: SUBMIT_DISABLED_BG, color: SUBMIT_DISABLED_FG },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
      <CancelMatchDialog
        open={cancelOpen}
        onKeepMatching={() => setCancelOpen(false)}
        onConfirmCancel={() => {
          setCancelOpen(false);
          navigate('/data');
        }}
      />
    </Box>
  );
}
